import React, { useState, useEffect } from 'react';
import { followUpsAPI, clientsAPI } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { User, Check, Trash2, CalendarIcon, Plus, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const FollowUpsPage = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [followUps, setFollowUps] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Form state
  const [customerType, setCustomerType] = useState('existing');
  const [selectedClient, setSelectedClient] = useState('');
  const [reminderType, setReminderType] = useState('');
  const [message, setMessage] = useState('');
  const [dueDate, setDueDate] = useState(new Date());

  const fetchFollowUps = async () => {
    setLoading(true);
    try {
      const completed = activeTab === 'completed';
      const response = await followUpsAPI.getAll(completed);
      setFollowUps(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({ limit: 100 });
      setClients(response.data.clients);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchFollowUps();
    fetchClients();
  }, [activeTab]);

  const handleComplete = async (id) => {
    try {
      await followUpsAPI.update(id, { completed: true, status: 'Completed' });
      setFollowUps(followUps.filter(f => f.id !== id));
    } catch (error) {
    }
  };

  const handleDelete = async (id) => {
    try {
      await followUpsAPI.delete(id);
      setFollowUps(followUps.filter(f => f.id !== id));
    } catch (error) {
    }
  };

  const handleCreateFollowUp = async () => {
    try {
      const client = clients.find(c => c.id === selectedClient);
      const newFollowUp = {
        client_name: client?.name || 'Unknown Client',
        description: message,
        type: reminderType,
        status: 'Pending',
        completed: false,
        last_ordered: null
      };
      const response = await followUpsAPI.create(newFollowUp);
      setFollowUps([response.data, ...followUps]);
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
    }
  };

  const resetForm = () => {
    setCustomerType('existing');
    setSelectedClient('');
    setReminderType('');
    setMessage('');
    setDueDate(new Date());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Follow-ups</h1>
          <p className="text-gray-500">Manage client reminders and follow-ups</p>
        </div>
        <Button 
          onClick={() => setIsModalOpen(true)}
          className="bg-[#4F46E5] hover:bg-[#4338CA] text-white h-10"
        >
          <Plus size={16} className="mr-2" />
          New Follow-up
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'pending'
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'completed'
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Completed
        </button>
      </div>

      {/* Follow-ups List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
        </div>
      ) : (
        <div className="space-y-4">
          {followUps.length === 0 ? (
            <Card className="p-8 bg-white border border-gray-100 text-center">
              <p className="text-gray-500">No {activeTab} follow-ups</p>
            </Card>
          ) : (
            followUps.map((followUp) => (
              <Card key={followUp.id} className="p-6 bg-white border border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="text-gray-500" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{followUp.client_name}</h3>
                      <p className="text-gray-600 mt-1">{followUp.description}</p>
                      <div className="flex items-center gap-3 mt-3">
                        {followUp.last_ordered && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                            Last ordered {followUp.last_ordered}
                          </span>
                        )}
                        {followUp.type && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                            {followUp.type}
                          </span>
                        )}
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          followUp.status === 'Overdue'
                            ? 'bg-red-100 text-red-600'
                            : followUp.status === 'Completed'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-yellow-100 text-yellow-600'
                        }`}>
                          {followUp.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!followUp.completed && (
                      <button
                        onClick={() => handleComplete(followUp.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Mark as complete"
                      >
                        <Check className="text-gray-500" size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(followUp.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="text-gray-500 hover:text-red-500" size={18} />
                    </button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Create Follow-up Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Follow-up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Customer Type</Label>
              <Select value={customerType} onValueChange={setCustomerType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing">Existing Client</SelectItem>
                  <SelectItem value="potential">Potential Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Select Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reminder Type</Label>
              <Select value={reminderType} onValueChange={setReminderType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Follow up on inquiry">Follow up on inquiry</SelectItem>
                  <SelectItem value="Pending payment">Pending payment</SelectItem>
                  <SelectItem value="Last ordered">Last ordered</SelectItem>
                  <SelectItem value="Restock reminder">Restock reminder</SelectItem>
                  <SelectItem value="Partnership discussion">Partnership discussion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Enter reminder message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={setDueDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button
              onClick={handleCreateFollowUp}
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              disabled={!selectedClient || !message}
            >
              Create Follow-up
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FollowUpsPage;
